import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import ImmersiveLayout from '../components/ImmersiveLayout';
import { useLanguage } from '../contexts/LanguageContext';
import { ArrowRight, Home, Sparkles, Search } from 'lucide-react';

const EASE = [0.22, 1, 0.36, 1];

export default function NotFoundPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const id = language === 'id';

  return (
    <ImmersiveLayout>
      <section
        className="relative min-h-[100vh] flex items-center justify-center px-6 py-32 overflow-hidden"
        data-testid="notfound-page"
      >
        {/* ambient gradient */}
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute -top-32 left-1/4 w-[520px] h-[520px] rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(212,133,155,0.18) 0%, rgba(18,14,13,0) 70%)', filter: 'blur(40px)' }}
          />
          <div
            className="absolute -bottom-40 right-1/5 w-[460px] h-[460px] rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(197,219,123,0.14) 0%, rgba(18,14,13,0) 70%)', filter: 'blur(40px)' }}
          />
        </div>

        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="eyebrow-immersive"
          >
            {id ? 'Halaman tidak ditemukan' : 'Page not found'}
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 20, filter: 'blur(8px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0)' }}
            transition={{ duration: 1.1, ease: EASE, delay: 0.1 }}
            className="font-heading text-7xl sm:text-8xl lg:text-[10rem] text-seikat-bone leading-[0.95] mt-4"
            data-testid="notfound-headline"
          >
            4<em className="text-seikat-pink">0</em>4
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.3 }}
            className="mt-6 font-heading italic text-xl sm:text-2xl text-seikat-bone-muted max-w-xl mx-auto leading-relaxed"
          >
            {id
              ? '"Beberapa kelopak hilang dalam perjalanan — mari kembali ke rangkaian utama."'
              : '"A few petals lost their way — let\'s return to the main weave."'}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.45 }}
            className="mt-12 flex flex-wrap items-center justify-center gap-3"
          >
            <button
              onClick={() => navigate('/')}
              data-testid="notfound-home"
              data-magnet
              className="btn-pill btn-pill--solid"
            >
              <Home size={14} /> {id ? 'Kembali ke Beranda' : 'Back to Home'}
            </button>
            <button
              onClick={() => navigate('/catalog')}
              data-testid="notfound-catalog"
              data-magnet
              className="btn-pill"
            >
              <Search size={14} /> {id ? 'Jelajahi Bouquet' : 'Browse Bouquets'}
            </button>
            <button
              onClick={() => navigate('/memory-bouquet')}
              data-testid="notfound-memory"
              data-magnet
              className="btn-pill"
            >
              <Sparkles size={14} /> {id ? 'Memory Bouquet' : 'Memory Bouquet'}
              <ArrowRight size={12} />
            </button>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="mt-20 text-xs tracking-[0.32em] uppercase text-seikat-bone-muted"
          >
            — Seikat Atelier
          </motion.p>
        </div>
      </section>
    </ImmersiveLayout>
  );
}
